import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    LinearProgress,
    Chip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    IconCloudUpload,
    IconPlayerPlay,
    IconRefresh,
    IconDatabase,
    IconCloud,
    IconCheck,
    IconX,
    IconClock,
    IconAlertCircle
} from '@tabler/icons-react';
import { gridSpacing } from 'store/constant';
import {
    useGetStorageMigrationStatusQuery,
    useGetStorageAnalyticsQuery,
    useStartStorageMigrationMutation
} from 'store/slices/apiSlice';

const MigrationDashboard = () => {
    const theme = useTheme();
    const { data: migrationStatus, refetch: refetchMigration } = useGetStorageMigrationStatusQuery(undefined, { skip: false });
    const { data: analytics } = useGetStorageAnalyticsQuery(undefined, { skip: false });
    const [startMigrationMutation, { isLoading: loading }] = useStartStorageMigrationMutation();

    const [showStartDialog, setShowStartDialog] = useState(false);
    const [batchSize, setBatchSize] = useState(50);
    const [storageBackendFilter, setStorageBackendFilter] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            if (migrationStatus?.is_migration_running) {
                refetchMigration();
            }
        }, 10000);
        return () => clearInterval(interval);
    }, [migrationStatus?.is_migration_running, refetchMigration]);

    const handleStartMigration = async () => {
        try {
            await startMigrationMutation({
                batch_size: batchSize,
                storage_backend_filter: storageBackendFilter || null
            }).unwrap();
            setShowStartDialog(false);
            setTimeout(() => refetchMigration(), 2000);
        } catch (error) {
            console.error('Failed to start migration:', error);
        }
    };

    const getMigrationStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return theme.palette.success.main;
            case 'in_progress':
                return theme.palette.warning.main;
            case 'pending':
                return theme.palette.info.main;
            case 'failed':
                return theme.palette.error.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const getMigrationStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return <IconCheck size={20} />;
            case 'in_progress':
                return <IconClock size={20} />;
            case 'pending':
                return <IconAlertCircle size={20} />;
            case 'failed':
                return <IconX size={20} />;
            default:
                return <IconDatabase size={20} />;
        }
    };

    const progressCards = [
        {
            title: 'Total Files',
            value: migrationStatus?.total_files || 0,
            icon: <IconDatabase size={24} />,
            color: theme.palette.primary.main,
            description: 'Files in system'
        },
        {
            title: 'Migrated',
            value: migrationStatus?.migrated_files || 0,
            icon: <IconCloud size={24} />,
            color: theme.palette.success.main,
            description: 'Successfully migrated',
            percentage: migrationStatus?.migration_progress_percent || 0
        },
        {
            title: 'In Progress',
            value: migrationStatus?.in_progress_files || 0,
            icon: <IconCloudUpload size={24} />,
            color: theme.palette.warning.main,
            description: 'Currently migrating'
        },
        {
            title: 'Pending',
            value: migrationStatus?.pending_files || 0,
            icon: <IconClock size={24} />,
            color: theme.palette.info.main,
            description: 'Awaiting migration'
        },
        {
            title: 'Failed',
            value: migrationStatus?.failed_files || 0,
            icon: <IconX size={24} />,
            color: theme.palette.error.main,
            description: 'Migration errors'
        }
    ];

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconCloudUpload size={32} />
                    <Typography variant="h3">Lakehouse Migration</Typography>
                    {migrationStatus?.is_migration_running && (
                        <Chip label="Migration Running" color="warning" icon={<IconPlayerPlay size={16} />} />
                    )}
                </Box>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" startIcon={<IconRefresh />} onClick={() => refetchMigration()} disabled={loading}>
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<IconPlayerPlay />}
                        onClick={() => setShowStartDialog(true)}
                        disabled={migrationStatus?.is_migration_running}
                    >
                        Start Migration
                    </Button>
                </Box>
            </Box>

            {/* Overall Progress */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h4">Overall Progress</Typography>
                        <Typography variant="h3" color="primary">
                            {migrationStatus?.migration_progress_percent?.toFixed(1) || 0}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={migrationStatus?.migration_progress_percent || 0}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            mb: 2,
                            bgcolor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                bgcolor: theme.palette.success.main
                            }
                        }}
                    />
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="textSecondary">
                            {migrationStatus?.migrated_files || 0} of {migrationStatus?.total_files || 0} files migrated
                        </Typography>
                        {migrationStatus?.last_migration_at && (
                            <Typography variant="body2" color="textSecondary">
                                Last migration: {new Date(migrationStatus.last_migration_at).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Progress Cards */}
            <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                {progressCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                        <Card>
                            <CardContent>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box flex={1}>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            {card.title}
                                        </Typography>
                                        <Typography variant="h3" component="div" sx={{ mb: 0.5 }}>
                                            {card.value.toLocaleString()}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {card.description}
                                        </Typography>
                                        {card.percentage !== undefined && (
                                            <Box mt={1}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={card.percentage}
                                                    sx={{
                                                        height: 4,
                                                        borderRadius: 2,
                                                        bgcolor: `${card.color}20`,
                                                        '& .MuiLinearProgress-bar': {
                                                            bgcolor: card.color
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            backgroundColor: `${card.color}20`,
                                            borderRadius: '12px',
                                            p: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            ml: 1
                                        }}
                                    >
                                        {React.cloneElement(card.icon, { style: { color: card.color } })}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Storage Backend Breakdown */}
            {analytics?.files_by_backend && (
                <Grid container spacing={gridSpacing} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" gutterBottom>
                                    Files by Storage Backend
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <List>
                                    {Object.entries(analytics.files_by_backend).map(([backend, count]) => (
                                        <ListItem key={backend}>
                                            <ListItemIcon>
                                                {backend === 'minio-lakehouse' ? (
                                                    <IconCloud size={20} color={theme.palette.primary.main} />
                                                ) : (
                                                    <IconDatabase size={20} color={theme.palette.grey[500]} />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={backend === 'minio-lakehouse' ? 'MinIO Lakehouse' : backend.toUpperCase()}
                                                secondary={`${count} files`}
                                            />
                                            <Chip
                                                label={`${((count / analytics.total_files) * 100).toFixed(1)}%`}
                                                size="small"
                                                color={backend === 'minio-lakehouse' ? 'primary' : 'default'}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4" gutterBottom>
                                    Migration Status Breakdown
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <List>
                                    {analytics?.files_by_migration_status &&
                                        Object.entries(analytics.files_by_migration_status).map(([status, count]) => (
                                            <ListItem key={status}>
                                                <ListItemIcon sx={{ color: getMigrationStatusColor(status) }}>
                                                    {getMigrationStatusIcon(status)}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                                                    secondary={`${count} files`}
                                                />
                                                <Chip
                                                    label={`${((count / analytics.total_files) * 100).toFixed(1)}%`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: `${getMigrationStatusColor(status)}20`,
                                                        color: getMigrationStatusColor(status)
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Start Migration Dialog */}
            <Dialog open={showStartDialog} onClose={() => setShowStartDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Start Lakehouse Migration</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        This will start migrating files from legacy storage to MinIO Lakehouse. The migration runs in the background and can
                        be monitored from this dashboard.
                    </Alert>

                    <TextField
                        label="Batch Size"
                        type="number"
                        fullWidth
                        value={batchSize}
                        onChange={(e) => setBatchSize(parseInt(e.target.value) || 50)}
                        helperText="Number of files to migrate per batch"
                        sx={{ mb: 2 }}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Storage Backend Filter</InputLabel>
                        <Select
                            value={storageBackendFilter}
                            label="Storage Backend Filter"
                            onChange={(e) => setStorageBackendFilter(e.target.value)}
                        >
                            <MenuItem value="">All Backends</MenuItem>
                            <MenuItem value="gcs">GCS Only</MenuItem>
                            <MenuItem value="s3">S3 Only</MenuItem>
                            <MenuItem value="local">Local Only</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowStartDialog(false)}>Cancel</Button>
                    <Button onClick={handleStartMigration} variant="contained" startIcon={<IconPlayerPlay />} disabled={loading}>
                        Start Migration
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MigrationDashboard;
